import AppLayout from '@/layout/AppLayout.vue';
import { createRouter, createWebHistory } from 'vue-router';
import { hasKnownAuthenticatedSession, useAuthState } from '@/composables/authState.js';
import { clearMyBotsCache, readLastMyBotsContext } from '@/utils/myBotsCache.js';
import { authService } from '@/services/authService.js';
import { installNavigationRecovery } from '@/composables/useNavigationRecovery.js';
import { clearUserContext } from '@/composables/userContext.js';
import { restoreDesktopPageScroll } from '@/utils/desktopPageScroll.js';

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior(to, from, savedPosition) {
        if (restoreDesktopPageScroll(to, from, savedPosition)) return {};
        if (savedPosition) {
            // Preserve browser position when using back/forward navigation
            return window.__CORECRY_IOS_BUILD__ ? { ...savedPosition, behavior: 'instant' } : savedPosition;
        }
        if (from.name && to.path !== from.path) {
            // Reset scroll only when navigating to a different page
            return { top: 0 };
        }
        // Keep current scroll position on initial load or reload
        return {};
    },
    routes: [
        {
            path: '/',
            component: AppLayout,
            children: [
                {
                    path: '/notifications',
                    name: 'notifications',
                    component: () => import('@/pages/Notifications.vue')
                },
                {
                    path: '/',
                    redirect: '/home'
                },
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/pages/Home.vue')
                },
                {
                    path: '/my-bots',
                    name: 'my-bots',
                    component: () => import('@/pages/MyBots.vue')
                },
                {
                    path: '/trade-journal',
                    name: 'trade-journal',
                    component: () => import('@/pages/TradeJournal.vue')
                },
                {
                    path: '/statistics',
                    name: 'statistics',
                    component: () => import('@/pages/Statistics.vue')
                },
                {
                    path: '/apikeys',
                    name: 'apikeys',
                    component: () => import('@/pages/ApiKeys.vue')
                },
                {
                    path: '/logs',
                    name: 'logs',
                    redirect: { path: '/settings', query: { tab: 'logs' } }
                },
                {
                    path: '/settings',
                    name: 'settings',
                    component: () => import('@/pages/Settings.vue')
                },
                {
                    path: '/customers',
                    name: 'customers',
                    redirect: { path: '/settings', query: { tab: 'customers' } }
                },
                {
                    path: '/create-bot',
                    name: 'create-bot',
                    component: () => import('@/pages/BotEditorPage.vue'),
                    meta: { botEditorMode: 'create' }
                },
                {
                    path: '/edit-bot/:id',
                    name: 'edit-bot',
                    component: () => import('@/pages/BotEditorPage.vue'),
                    meta: { botEditorMode: 'edit' }
                },
            ]
        },

        {
            path: '/auth/login',
            name: 'login',
            component: () => import('@/pages/Login.vue')
        }
    ]
});

const { getLoggingOutState, setStatus } = useAuthState();
let pendingKnownSessionValidation = null;

const isAuthorizedResponse = (data) => Boolean(
    data?.status === 'authorized' && !data?.explicitly_logged_out
);

const validateKnownSession = () => {
    if (!pendingKnownSessionValidation) {
        pendingKnownSessionValidation = authService.getStatus()
            .then((data) => {
                const authorized = isAuthorizedResponse(data);
                if (!authorized) { clearMyBotsCache(); clearUserContext(); }
                setStatus(authorized ? 'authenticated' : 'logged-out');
                return authorized;
            })
            .catch((error) => {
                globalThis.clientLogger?.error('Auth status check failed during background reload validation:', error);
                setStatus('logged-out');
                return false;
            })
            .finally(() => {
                pendingKnownSessionValidation = null;
            });
    }
    return pendingKnownSessionValidation;
};

const redirectIfKnownSessionIsInvalid = () => {
    void validateKnownSession().then((authorized) => {
        if (!authorized && !router.currentRoute.value.path.startsWith('/auth')) {
            void router.replace('/auth/login').catch(() => {});
        }
    });
};

const canRenderProtectedRouteImmediately = (to) => Boolean(
    hasKnownAuthenticatedSession()
    || (to.path === '/my-bots' && readLastMyBotsContext())
);

router.beforeEach(async (to, from, next) => {
    try {
        // Check if we're in the process of logging out
        if (getLoggingOutState()) {
            // During logout process, allow navigation to auth pages
            if (to.path.startsWith('/auth')) {
                next();
            } else {
                next('/auth/login');
            }
            return;
        }

        // Public authentication pages must render immediately. Login performs
        // its own status check and redirects an already authorized user.
        if (to.path.startsWith('/auth')) {
            next();
            return;
        }

        // A previously authenticated tab can render its cached route immediately
        // while the session is revalidated in the background. This removes the
        // blank navigation gap on a browser reload without trusting the cache for
        // authorization: an invalid session is redirected as soon as it is known.
        if (canRenderProtectedRouteImmediately(to)) {
            setStatus('authenticated');
            next();
            redirectIfKnownSessionIsInvalid();
            return;
        }

        // Always check personal authentication status for all users
        try {
            const data = await authService.getStatus();

            if (data) {

                // User is properly authenticated and not explicitly logged out
                if (data && data.status === 'authorized' && !data.explicitly_logged_out) {
                    const cachedContext = readLastMyBotsContext();
                    if (cachedContext && data.user_id && !cachedContext.startsWith(`${data.user_id}:`)) {
                        clearMyBotsCache();
                    }
                    setStatus('authenticated');
                    if (to.path === '/my-bots') {
                        void import('@/pages/MyBots.vue');
                    }
                    // Authenticated user trying to access login page - redirect to home
                    if (to.path.startsWith('/auth')) {
                        next('/');
                    } else {
                        // Allow access to protected pages
                        next();
                    }
                    return;
                }

                // User was explicitly logged out or not authorized
                if (data && (data.explicitly_logged_out || data.status !== 'authorized')) {
                    clearMyBotsCache();
                    clearUserContext();
                    setStatus('logged-out');
                    // Allow access to auth pages
                    if (to.path.startsWith('/auth')) {
                        next();
                    } else {
                        // Redirect to login for protected pages
                        next('/auth/login');
                    }
                    return;
                }
            }

            // If we reach here, either response was not ok or data was invalid
            // Treat as unauthenticated
            setStatus('logged-out');
            if (to.path.startsWith('/auth')) {
                next();
            } else {
                next('/auth/login');
            }

        } catch (error) {
            globalThis.clientLogger?.error('Auth status check failed:', error);
            // On network error, treat as unauthenticated
            setStatus('logged-out');
            if (to.path.startsWith('/auth')) {
                next();
            } else {
                next('/auth/login');
            }
        }
        
    } catch (error) {
        globalThis.clientLogger?.error('Router guard error:', error);
        // On any unexpected error in router guard, allow navigation to proceed
        // This prevents the router from getting stuck
        next();
    }
});

installNavigationRecovery(router);
router.beforeEach(async (to, from) => {
    if (to.name !== 'create-bot') return true;
    const { checkBotCreationLimit } = await import('@/composables/useBotCreationLimitGate.js');
    if (await checkBotCreationLimit()) return true;
    return from.name ? false : '/home';
});

export default router;
